import { API_URL, STORAGE_API_URL } from "../components/common/Constants";
import { IStandardAPIResponse, ITokenValidation } from "../vm";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { getFileNameError } from "../utils/fileName";
import { ensureMsalInitialized, msalInstance, MS_SSO_MODE_KEY } from "../authConfig";

// Wipe local Microsoft SSO state on an involuntary logout so the login page's
// silent probe starts from a clean slate — no cached account/ID token and no
// stale silent/interactive marker can leak into the next login attempt. Local
// only: never signs the user out of Microsoft.
const clearMsalCache = async () => {
  try {
    sessionStorage.removeItem(MS_SSO_MODE_KEY);
    await ensureMsalInitialized();
    if (msalInstance.getAllAccounts().length > 0) {
      await msalInstance.clearCache();
    }
  } catch (e) {
    console.error('MSAL cache clear failed (non-fatal):', e);
  }
};

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem("morAccessToken", accessToken);
  localStorage.setItem("morRefreshToken", refreshToken);
};

export const setToken = (token: string) => {
  localStorage.setItem("morAccessToken", token);
};
export const removeToken = () => {
  localStorage.removeItem("morAccessToken");
  localStorage.removeItem("morRefreshToken");
};
export const groupBy = (data: Array<any>, prop: string) => {
  return data.reduce(function (groups, item) {
    const val = item[prop];
    groups[val] = groups[val] || [];
    groups[val].push(item);
    return groups;
  }, {});
};

export function getToken() {
  let res = localStorage.getItem("morAccessToken");
  if (res === null || res === undefined) {
    return "";
  }
  return res;
}

export function getRefreshToken() {
  let res = localStorage.getItem("morRefreshToken");
  if (res === null || res === undefined) {
    return "";
  }
  return res;
}
export const parseJwt = (tokenParsed?: string) => {
  let token;
  if (!tokenParsed) {
    token = getToken();
  } else {
    token = tokenParsed;
  }
  if (token) {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  }
  return undefined;
};

export const httpClient = async <T,>(
  url: string,
  type: string,
  obj: any = undefined,
  isLocalUrl: boolean = false,
  contentType = "application/json; charset=utf-8",
  token = getToken(),
  skipTokenRefresh = false,
  customHeaders: Record<string, string> = {}
): Promise<IStandardAPIResponse<T>> => {
  try {
    type = type.toUpperCase();
    if (type.toLowerCase() === "get" && obj) {
      var params = Object.keys(obj)
        .map(function (key) {
          if (
            typeof obj[key] === "object" &&
            obj[key].length &&
            obj[key].length > 0
          ) {
            let a = obj[key].map((subKey: string) => {
              return key + "[]=" + subKey;
            });
            console.log("a", a);
            return a.join("&");
          } else {
            return key + "=" + obj[key];
          }
        })
        .join("&");
      url += "?" + params;
      obj = undefined;
    }
    let apiUrl = API_URL;
    if (isLocalUrl) {
      apiUrl = "";
    }

    // Handle authentication
    const authValidationResp = await handleAuthentication(url, token, skipTokenRefresh);
    if (authValidationResp.success) {
      token = authValidationResp.data || token;
    } else {
      return authValidationResp as any;
    }

    // Make the request
    let res = await fetch(apiUrl + url, {
      method: type.toUpperCase(),
      body: JSON.stringify(obj),
      credentials: 'include', // Enable credentials for CORS
      headers: {
        "Content-Type": contentType,
        at: token,
        ...customHeaders
      },
    });

    // Handle authentication response
    return await handleAuthenticationResponse(res, url, skipTokenRefresh, (updatedToken: string) =>
      fetch(apiUrl + url, {
        method: type.toUpperCase(),
        body: JSON.stringify(obj),
        credentials: 'include', // Enable credentials for CORS
        headers: {
          "Content-Type": contentType,
          at: updatedToken || getToken(),
          ...customHeaders
        },
      })
    );
  } catch (error) {
    console.group(`API ${type} Error`);
    console.error(error);
    console.groupEnd();
    throw error;
  }
};
export const httpFileUpload = async <T,>(
  url: string,
  type: string,
  obj: any,
  isStorageApi = false,
  token = getToken(),
  skipTokenRefresh = false
): Promise<IStandardAPIResponse<T>> => {
  try {
    type = type.toUpperCase();

    // Reject invalid filenames upfront (mirrors storage-api rule) before any
    // network call so the user gets immediate feedback.
    if (obj?.file) {
      const fileNameError = getFileNameError(obj.file.name);
      if (fileNameError) {
        return { success: false, message: fileNameError, data: null } as any;
      }
    }

    // Handle authentication
    const authValidationResp = await handleAuthentication(url, token, skipTokenRefresh);
    if (authValidationResp.success) {
      token = authValidationResp.data || token;
    } else {
      return authValidationResp as any;
    }

    let fd = new FormData();
    fd.append("file", obj.file);
    obj.data = obj?.data || {};

    Object.keys(obj.data).forEach((key) => {
      fd.append(key, obj.data[key]);
    });
    const CORRECT_API_URL = isStorageApi ? STORAGE_API_URL : API_URL;
    let res = await fetch(CORRECT_API_URL + url, {
      method: type.toUpperCase(),
      body: fd,
      credentials: 'include', // Enable credentials for CORS
      headers: {
        at: token,
      },
    });

    // Handle authentication response
    return await handleAuthenticationResponse(res, url, skipTokenRefresh, (updatedToken: string) =>
      fetch(CORRECT_API_URL + url, {
        method: type.toUpperCase(),
        body: fd,
        credentials: 'include', // Enable credentials for CORS
        headers: {
          at: updatedToken || getToken(),
        },
      })
    );
  } catch (error) {
    console.group(`API ${type} Error`);
    console.error(error);
    console.groupEnd();
    throw error;
  }
};

// Enhanced token validation
export const isTokenValid = (token?: string): ITokenValidation => {
  try {
    const tokenToCheck = token || getToken();
    if (!tokenToCheck) {
      return { isValid: false, isExpired: false, error: "No token found" };
    }

    const user = parseJwt(tokenToCheck);
    if (!user) {
      return { isValid: false, isExpired: false, error: "Invalid token structure" };
    }

    const currentTime = new Date().getTime() / 1000;

    if (user.exp && currentTime > user.exp) {
      return { isValid: true, isExpired: true, error: "Token expired" };
    }

    return { isValid: true, isExpired: false };
  } catch (error) {
    return { isValid: false, isExpired: false, error: "Token parsing failed" };
  }
};

// Update existing isTokenExpired to use new validation
export const isTokenExpired = () => {
  return !isAuthenticated();
};

const isAuthenticated = () => {
  const accessToken = getToken();
  const refreshToken = getRefreshToken();

  // no tokens at all = not authenticated
  if (!accessToken && !refreshToken) {
    return false;
  }

  // if we have an access token, check if it's valid (not expired, proper format)
  if (accessToken) {
    const accessValidation = isTokenValid(accessToken);
    if (accessValidation.isValid && !accessValidation.isExpired) {
      // access token is valid and not expired
      return true;
    }
  }

  // if access token is invalid/expired, check refresh token
  if (refreshToken) {
    const refreshValidation = isTokenValid(refreshToken);
    if (refreshValidation.isValid && !refreshValidation.isExpired) {
      // refresh token is valid - let HTTP client handle the refresh
      return true;
    }
  }

  // both tokens are invalid/expired or missing
  return false;
};

// Reusable authentication handler for HTTP requests
const handleAuthentication = async (
  url: string,
  token: string,
  skipTokenRefresh: boolean
): Promise<IStandardAPIResponse<string>> => {
  // Skip auth logic for certain endpoints
  if (skipTokenRefresh || url.includes("auth/refresh-token") || url.includes("auth-web-login")) {
    return { success: true, message: "", data: token };
  }

  // Pre-flight token validation
  const tokenValidation = isTokenValid(token);

  if (!tokenValidation.isValid && !tokenValidation.isExpired) {
    // Token is completely invalid (tampered/malformed)
    console.log("Invalid token detected, forcing logout");
    removeToken();
    clearMsalCache();
    setTimeout(() => window.location.href = "/login", 2000);
    return { success: false, message: "Invalid authentication token. You will be redirecting to login page...", data: "" };
  }

  if (tokenValidation.isExpired) {
    // Token is valid but expired, try to refresh
    console.log("Token expired, attempting refresh...");
    const newTokens = await refreshAccessToken();
    if (newTokens) {
      setTokens(newTokens.token, newTokens.refreshToken);
      return { success: true, message: "", data: newTokens.token };
    } else {
      // Refresh failed
      removeToken();
      clearMsalCache();
      setTimeout(() => window.location.href = "/login", 2000);
      return { success: false, message: "Session expired. You will be redirecting to login page...", data: "" };
    }
  }

  return { success: true, message: "", data: token };
};

// Handle authentication errors from response status codes
const handleAuthenticationResponse = async (
  res: Response,
  url: string,
  skipTokenRefresh: boolean,
  retryRequest: (updatedToken: string) => Promise<Response>
): Promise<any> => {
  // Skip auth logic for certain endpoints
  if (skipTokenRefresh || url.includes("auth/refresh-token") || url.includes("auth-web-login")) {
    return await res.json();
  }

  if (res.status === 401) {
    // 401 = Expired token, try refresh
    console.log("Token expired, attempting refresh...");
    const newTokens = await refreshAccessToken();
    if (newTokens) {
      setTokens(newTokens.token, newTokens.refreshToken);

      // Retry the original request
      const retryRes = await retryRequest(newTokens.token);
      return await retryRes.json();
    } else {
      removeToken();
      clearMsalCache();
      setTimeout(() => window.location.href = "/login", 2000);
      return { success: false, message: "Session expired. You will be redirecting to login page..." };
    }
  } else if (res.status === 403) {
    // 403 = Invalid/missing token, force logout immediately
    const apiResp = await res.json();
    console.log(`${apiResp.message || "Access forbidden"}, forcing logout`);
    removeToken();
    clearMsalCache();
    setTimeout(() => window.location.href = "/login", 2000);
    // need to show the error message from backend as we have multiple scenario here
    return { success: false, message: `${apiResp.message || "Your login session is invalid."} You will be redirecting to login page...` };
  }

  return await res.json();
};

// Deduplicates concurrent refresh attempts: when several parallel requests hit
// a 401 together, they all await the single in-flight refresh instead of racing
// the same refresh token (the losers would otherwise invalidate the freshly
// rotated session and needlessly kick the user to the login page).
let refreshTokenPromise: Promise<{token: string, refreshToken: string} | null> | null = null;

export const refreshAccessToken = (): Promise<{token: string, refreshToken: string} | null> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = doRefreshAccessToken().finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const doRefreshAccessToken = async (): Promise<{token: string, refreshToken: string} | null> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.log("No refresh token available");
      return null;
    }

    // Validate refresh token before sending
    const refreshValidation = isTokenValid(refreshToken);
    if (!refreshValidation.isValid) {
      console.log("Refresh token is invalid:", refreshValidation.error);
      removeToken(); // Clear invalid tokens
      return null;
    }

    if (refreshValidation.isExpired) {
      console.log("Refresh token is expired");
      removeToken(); // Clear expired tokens
      return null;
    }

    const result = await httpClient<{token: string, refreshToken: string}>(
      "auth/refresh-token",
      "POST",
      { refreshToken },
      false,
      "application/json; charset=utf-8",
      "", // No token needed for refresh endpoint
      true // skipTokenRefresh = true to prevent infinite loops
    );

    if (result.success && result.data && result.data.token && result.data.refreshToken) {
      console.log("Token refreshed successfully");
      return {
        token: result.data.token,
        refreshToken: result.data.refreshToken
      };
    } else {
      console.log("Token refresh failed:", result.message);
      return null;
    }

  } catch (error) {
    console.error("Token refresh failed:", error);
    return null;
  }
};

export const isAdmin = () => {
  var token = getToken();
  if (token) {
    let user = parseJwt(token);
    if (
      user &&
      (user.role === 0 || user.role === 2 || user.role === 3 || user.role === 1 || user.role === 4 || user.role === 5)
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};
export const ADMIN_USER_ROLES =  [0, 3, 4];
// using for password policy, min length of the password
export const isAdminUser = (userToken?: any) => {
  var token = userToken || getToken();
  if (token) {
    let user = parseJwt(token);
    return user.isAdmin && ADMIN_USER_ROLES.includes(user.role) ? true : false;
  } else {
    return false;
  }
};
export const getRoleId = () => {
  var token = getToken();
  if (token) {
    let user = parseJwt(token);
    if (user) {
      return user.role;
    }
  }
  return undefined;
};

export const getUserPermissionForDataCenter = (
  dataCenterId: string
): string | any => {
  var token: any = getToken();
  if (token) {
    let user = parseJwt();
    if (user.dataCenters && user.dataCenters.length > 0) {
      var dataCenterPermission = user.dataCenters.find(
        (x: any) => x.dataCenterId === dataCenterId
      );
      if (dataCenterPermission) {
        return dataCenterPermission.permission;
      }
    }
    return undefined;
  }
  return undefined;
};
export const isDataCenterAvailable = (dataCenterId: string): boolean => {
  var token: any = getToken();
  if (token) {
    let user = parseJwt();
    if (user.dataCenters && user.dataCenters.length > 0) {
      var dataCenter = user.dataCenters.find(
        (x: any) => x.dataCenterId === dataCenterId
      );
      if (dataCenter) {
        return true;
      }
    }
    return false;
  }
  return false;
};
export const removeNulls = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === "object") {
      removeNulls(obj[key]);
    } else if (obj[key] === undefined || obj[key] === null || obj[key] === "") {
      delete obj[key];
    }
  });
  return obj;
};

export const handleErrorResp = (err: any): IStandardAPIResponse<any> => {
  console.error(err);
  return {
    success: false,
    data: err,
    message: "",
  };
};

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getQueryParams = (url: string) => {
  var urlParams: any = {};
  if (window.location.search) {
    let search = window.location.search.replace("?", "");
    if (search) {
      let params = search.split("&");
      params.forEach((element) => {
        let searchQuery = element.split("=");
        if (searchQuery && searchQuery.length > 1) {
          urlParams[searchQuery[0]] = searchQuery[1];
        }
      });
    }
  }
  // var urlParams = {},
  //   match,
  //   additional = /\+/g, // Regex for replacing additional symbol with a space
  //   search = /([^&=]+)=?([^&]*)/g,
  //   decode = function(s: string) {
  //     return decodeURIComponent(s.replace(additional, " "));
  //   },
  //   query;
  // if (url) {
  //   if (url.split("?").length > 0) {
  //     query = url.split("?")[1];
  //   }
  // } else {
  //   url = window.location.href;
  //   query = window.location.search.substring(1);
  // }
  // while (match === search.exec(query)) {
  //   urlParams[decode(match[1])] = decode(match[2]);
  // }
  return urlParams;
};

export const getPrecisedValue = (value: number) => {
  let preciseValue = String(value);
  if (preciseValue.includes(".")) {
    let a = preciseValue.split(".");
    return Number(a[0] + "." + a[1].substr(0, 2).padEnd(2, "0"));
  } else {
    return Number(preciseValue);
  }
};
export const numberWithCommas = (x: string) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
// export const exportExcelSheet = (csvData, fileName) => {
//   const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
//   const fileExtension = ".xlsx";
//   const ws = XLSX.utils.json_to_sheet(csvData);
//   const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
//   const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//   const data = new Blob([excelBuffer], { type: fileType });
//   FileSaver.saveAs(data, fileName + fileExtension);
// };

export const httpCustomUrlClient = async <T,>(
  url: string,
  type: string,
  obj: any = undefined
) => {
  try {
    type = type.toUpperCase();
    if (type.toLowerCase() === "get" && obj) {
      var params = Object.keys(obj)
        .map(function (key) {
          return key + "=" + obj[key];
        })
        .join("&");
      url += "?" + params;
      obj = undefined;
    }
    await fetch(url, {
      method: type.toUpperCase(),
      credentials: 'include', // Enable credentials for CORS
      body: obj,
    });
    return null;
  } catch (error) {
    console.group(`Custom API ${type} Error`);
    console.error(error);
    console.groupEnd();
    throw error;
  }
};

// export const getSignedUrl = async (
//   fileNames: Array<string>,
//   contentType: string
// ): Promise<IStandardAPIResponse<any>> => {
//   try {
//     let res = await httpClient<any>(`uploads`, 'POST', {
//       file: fileNames,
//     });
//     return res;
//   } catch (err) {
//     return err && err.response ? err.response.data : undefined;
//   }
// };

export const uploadFile = async (file: File): Promise<any> => {
  try {
    let res = await httpFileUpload<any>(`uploads`, "POST", { file }, true);
    return res;
  } catch (err: any) {
    return err && err.response ? err.response.data : undefined;
  }
};
export const uploadReportFile = async (
  file: File,
  fileName: string
): Promise<any> => {
  try {
    let res = await httpFileUpload<any>(
      `uploads-reports`,
      "POST",
      { file, data: { fileName } },
      true
    );
    return res;
  } catch (err: any) {
    return err && err.response ? err.response.data : undefined;
  }
};

export const getFileByBase64 = (base64: string, filename: string) => {
  var arr: any = base64.split(","),
    mime = arr[0].match(/:(.*?);/)[1];
  return fetch(base64)
    .then(function (res) {
      return res.arrayBuffer();
    })
    .then(function (buf) {
      return new File([buf], filename, { type: mime });
    });
};

export const exportExcelSheet = (csvData: any, fileName: any) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const ws = XLSX.utils.json_to_sheet(csvData);
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

/********************** Password Validations **************************** */
export const isValidPassword = (password: string, isAdmin?: boolean): boolean => {
  // contains lower case letter
  // contains upper case letter
  // contains numbers
  // contains special Char
  // min length 14 - admin user, 10 - other user, earlier it was 8
  // max length 25

  // token extraction
  let adminUser = isAdmin;
  if (adminUser === undefined) {
    adminUser = isAdminUser();
  }
  return (
    isContainLowerCase(password) &&
    isContainUpperCase(password) &&
    isContainNumber(password) &&
    isContainSpecialChar(password) &&
    isMinLength8(password, adminUser) &&
    isMaxLength25(password)
  );
};

export const isContainLowerCase = (str: string): boolean => {
  return str.match(/[a-z]/g) != null;
};
export const isContainUpperCase = (str: string): boolean => {
  return str.match(/[A-Z]/g) != null;
};
export const isContainNumber = (str: string): boolean => {
  return str.match(/[0-9]/g) != null;
};
export const isContainSpecialChar = (str: string): boolean => {
  return str.match(/[^a-zA-Z\d]/g) != null;
};
export const isMinLength8 = (str: string, isAdmin = false): boolean => {
  return isAdmin ? str.length >= 14 : str.length >= 10;
};
export const isMaxLength25 = (str: string): boolean => {
  return str.length <= 25;
};

/********************************* */

// 🚫 PASSWORD EXPIRY — NOT IN USE. Its only caller was the post-login expiry
// alert in components/auth/LoginMain.tsx, which is commented out along with the
// provider mount in App.tsx. The endpoint it reads (GET
// last-password-updated-time) is unregistered on the API too.
export const passwordUpdatedDaysAgo = async () => {
  const res = await httpClient<{ date?: Date }>(
    `last-password-updated-time`,
    "GET"
  );
  if (res && res.success && res.data.date) {
    const date = new Date(res.data.date);
    return timeSinceInDays(date);
  }
  return null;
};

const timeSinceInDays = (date: Date) => {
  const seconds: number = Math.floor(
    (new Date().getTime() - date.getTime()) / 1000
  );

  var interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval);
  }
  return 0;
};

export const toFixedValue = (value: any) => {
  if (typeof value === "number") {
    return value.toFixed(2);
  } else {
    if (isNaN(parseFloat(value))) {
      return "NA";
    } else {
      return parseFloat(value).toFixed(2);
    }
  }
};

export const downloadFile = async (fileName: string, fileUrl: string) => {
  const result = await fetch(fileUrl, {
    method: "GET",
    credentials: 'include', // Enable credentials for CORS
    headers: {
      // at: getToken(),
    },
  });
  const pptx = await result.blob();
  const pptxFile = new Blob([pptx], {
    type:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  });
  const pptxFileUrl = window.URL.createObjectURL(pptxFile);
  var a: any = document.createElement("a");

  a.download = `${fileName}`;
  a.href = pptxFileUrl;
  a.click();
};

export const getPercentageValue = (value: number, totalValue: number) => {
  if (value < 0) return;
  if (totalValue < 1) return 0;
  let number = (value / totalValue) * 100;
  return Number.isInteger(number) ? number : number.toFixed(2);
};

export const b64toBlob = async (b64Data: any, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

/**
 * Validates if a file type matches the accepted types
 * @param file The file to validate
 * @param acceptedTypes String from the accept prop (e.g. ".xlsx,.csv")
 * @returns Boolean indicating if the file type is valid
 */
export const validateFileType = (
  file: File,
  acceptedTypes: string
): boolean => {
  if (!acceptedTypes || !file) return true;

  // Convert accept string to array of valid extensions/mime types
  const validTypes = acceptedTypes
    .split(",")
    .map((type) => type.trim().toLowerCase());

  // Get file extension
  const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  const fileType = file.type.toLowerCase();

  // Check if file type/extension is in the accepted list
  return validTypes.some((type) => {
    // Check for mime type match (e.g. "image/png")
    if (type.includes("/")) {
      return fileType === type;
    }
    // Check for extension match (e.g. ".xlsx")
    return fileExtension === type;
  });
};

/**
 * Generates a standardized error message for file type validation
 * @param fileTypeDescription Description of accepted file types (e.g., "Excel files (.xlsx, .xls)")
 * @returns Formatted error message
 */
export const getFileTypeErrorMessage = (
  fileTypeDescription: string
): string => {
  return `File type not supported. Please upload ${fileTypeDescription} only.`;
};

// Get OS from userAgent
export function getOS() {
  const userAgent = window.navigator.userAgent || window.navigator.vendor || (window as any).opera;
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }
  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return "iOS";
  }
  if (/Win/i.test(userAgent)) {
    return "Windows";
  }
  if (/Mac/i.test(userAgent)) {
    return "MacOS";
  }
  if (/Linux/i.test(userAgent)) {
    return "Linux";
  }
  return "Unknown";
}

// Get browser from userAgent
export function getBrowser() {
  const userAgent = window.navigator.userAgent;
  // Order is important — check more specific identifiers first
  if (userAgent.includes("Edg/")) return "Edge"; // Chromium-based Edge
  if (userAgent.includes("OPR") || userAgent.includes("Opera")) return "Opera";
  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Trident/")) return "Internet Explorer";
  if (userAgent.includes("Chrome")) return "Chrome"; // Must come after Edge/Opera checks
  if (userAgent.includes("Safari")) return "Safari";
  return "Unknown";
}

// Get userAgent string
export function getUserAgent() {
  return window.navigator.userAgent;
}
